import { getModel } from '../../../model/registry'
import styled from "styled-components"

import CoreForm from "../../cardView/CoreForm"
import CardViewFormLines from "../../cardView/CardViewFormLines"

import useTranslation from "../../../hooks/useTranslation"
import useLocale from "../../../hooks/useLocale"
import sumFields from "../../../utils/sumFields"
import Warning from "../ModalWarning"
import { getNestedValue } from "../../../utils/objects/nestedValue"
import { retrieveParams } from "./retrieveParams"

const Backgrounder = styled.div`
    background-color: ${({ theme }) => theme.form.panelBg};
    padding: 1rem;
    margin-inline: -1rem;
`
const Description = styled.p`
    margin-top: 0;
`
const Footer = styled.p`
    font-weight: 600;
`

/**
 * A card section, inside a modal.
 *
 * Named for what it renders, in both of its modes. It was briefly `sectionList`,
 * which was wrong twice over: it renders one section rather than a list of them,
 * and `cardSection` — the name one of the two merged widgets already had — is
 * true whether the section is referenced or written out longhand.
 *
 * This was two widgets. `cardSection` rendered a section of a `cardViewData`
 * card, named by reference; `lineList` rendered one written longhand inside
 * `modalFormData`, wrapped in a description and a total. Same noun, and the
 * difference was where the section came from — which is `modalFormRef` versus
 * `modalForm` one level down, and 33.1 §7 already ruled on that shape.
 *
 *   sectionRef: { view: 'productCard', section: 0 }   by reference
 *   section: { id: …, meta: …, … }                    inline
 *
 * `section` used to mean an index under `cardSection` and the whole object
 * under `lineList` — one name for two things, which is why the reference form
 * takes its index inside `sectionRef` and `section` now only ever means the
 * section itself.
 *
 * **The frame is optional**, and only the inline config has ever asked for one.
 *
 * **The renderer is not a third key.** §5.2 counted two differences and there
 * were three: `cardSection` renders fields through `CoreForm`, `lineList`
 * renders rows through `CardViewFormLines`. But the section already says which
 * it is — `meta.type: 'lines'` is in the config today — so this reads the
 * declaration rather than adding one.
 *
 * A referenced section's own `buttons` are dropped: in a modal the footer is the
 * modal's, so rendering them here would put a second Update button in the body.
 */
const CardSection = ({ element, entity, validated, useStore, path = [], options, origin }) => {
    const { m } = useTranslation()
    const { lf } = useLocale()

    const [state] = useStore(s => path.length === 0 ? s : getNestedValue(s, path))

    const ref = element?.sectionRef
    const referenced = ref
        ? getModel('cardViewData')[ref.view]?.sections?.[ref.section ?? 0]
        : null
    const section = ref ? referenced : element?.section

    if (!section) {
        console.warn(`[cardSection] no section for ${ref ? `view '${ref.view}'` : `element '${element?.id}'`}`)
        return null
    }

    if (section.meta?.type === 'lines') {
        const list = state?.[section.meta.linesArray] || []
        const total = sumFields(list, element.footer?.sumField)

        const bodyParams = element.body?.mergeParams ? retrieveParams(element.body.mergeParams, state, lf) : []
        const footerParams = element.footer?.mergeParams ? retrieveParams(element.footer.mergeParams, state, lf) : []

        return (
            <>
                {element.body?.description && <Description>{m(element.body.description, bodyParams)}</Description>}
                <Backgrounder>
                    <CardViewFormLines
                        expanded={false}
                        toggleExpanded={() => { }}
                        section={section}
                        linesArray={section.meta.linesArray}
                        entity={entity}
                        key={`${section.id}`}
                        dispatch={() => { }}
                        validated={validated}
                        useStore={useStore}
                        path={path}
                    />
                </Backgrounder>
                {element.footer?.description && <Footer>{m(element.footer.description, footerParams)}</Footer>}
                {element.footer?.warning &&
                    <Warning warning={element.footer.warning} state={state} compareValue={total} />}
            </>
        )
    }

    const { buttons, ...bodyOnly } = section

    return (
        <CoreForm
            section={bodyOnly}
            options={options}
            expanded={true}
            toggleSection={() => { }}
            dispatch={() => { }}
            validated={validated}
            entity={entity}
            useStore={useStore}
            path={path}
            origin={origin}
        />
    )
}

export default CardSection
